import { useInviteToken } from '@/components/auth/useInviteToken';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { getConcordPaymentDate } from '@/helpers/dates';
import { digitsOnlyBeforeInput, formatCentsInput } from '@/helpers/inputHelpers';
import {
  useGetAccountWithTokenQuery,
  useGetDirectPaymentInfoQuery,
  usePostOneTimePaymentMutation,
} from '@/services/accounts';
import {
  PalmettoButton,
  PalmettoIcon,
  PalmettoOptionGroup,
  PalmettoPaymentMethodIcon,
  PalmettoSelect,
  PalmettoTextInput,
  PalmettoToggleGroup,
} from '@palmetto/dsm-components';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ApplicantType } from 'types';

import { CustomerDirectPaymentAchForm } from './CustomerDirectPaymentAchForm';
import { CustomerDirectPaymentCardForm } from './CustomerDirectPaymentCardForm';
import { CustomerDirectPaymentError } from './CustomerDirectPaymentError';
import { CustomerDirectPaymentExpiredLink } from './CustomerDirectPaymentExpiredLink';
import { CustomerDirectPaymentSuccess } from './CustomerDirectPaymentSuccess';

const LOGO_STYLE = { height: '32px' };
const PAYMENT_METHOD_ITEMS = [
  { label: 'Credit/Debit', value: 'credit' },
  { label: 'ACH Transfer', value: 'ach' },
];
const INVITE_TOKEN_EXPIRED_OR_USED_STATUS = 401;
const PAYMENT_FREQUENCY_OPTIONS = [
  {
    value: 'autopay',
    label: 'Pay now, and enroll in Autopay',
    subline: 'The cheapest monthly bill for your account',
  },
  {
    value: 'oneTime',
    label: 'Keep this a one-time payment',
    subline: 'Without Autopay, a $15 fee will be added to your bill',
  },
];

type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover';

function detectCardBrand(cardNumber: string): CardBrand | null {
  const digits = cardNumber.replace(/\D/g, '');
  if (!digits) return null;
  if (digits[0] === '4') return 'visa';
  if (digits[0] === '5' || /^2[2-7]/.test(digits)) return 'mastercard';
  if (digits[0] === '3') return 'amex';
  if (digits[0] === '6') return 'discover';
  return null;
}

const formatCurrency = (n: number) => currencyFormatter.format(n);

type PaymentMethod = 'credit' | 'ach';
type PaymentAmountOption = 'pastDue' | 'totalDue' | 'custom';

function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number') {
    return error.status;
  }
  return undefined;
}

export function CustomerDirectPayment() {
  const { id = '' } = useParams<{ id: string }>();
  const { inviteToken } = useInviteToken();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [zip, setZip] = useState('');
  const [nameOnCard, setNameOnCard] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [bankAccountType, setBankAccountType] = useState<'checking' | 'savings'>('checking');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [paymentAmountOption, setPaymentAmountOption] = useState<PaymentAmountOption>('totalDue');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState<'autopay' | 'oneTime' | null>(null);

  const { data: account, isLoading, error: accountError } = useGetAccountWithTokenQuery({ id, inviteToken });
  const {
    data: directPaymentInfo,
    isLoading: isDirectPaymentInfoLoading,
    error: directPaymentInfoError,
  } = useGetDirectPaymentInfoQuery({ id, inviteToken }, { skip: !inviteToken });
  const minAllowedPayment =
    directPaymentInfo?.minAllowedPayment && directPaymentInfo.minAllowedPayment > 0
      ? directPaymentInfo.minAllowedPayment
      : 1;
  const accountErrorStatus = getErrorStatus(accountError);
  const directPaymentInfoErrorStatus = getErrorStatus(directPaymentInfoError);
  const isLinkInvalid =
    !inviteToken ||
    accountErrorStatus === INVITE_TOKEN_EXPIRED_OR_USED_STATUS ||
    directPaymentInfoErrorStatus === INVITE_TOKEN_EXPIRED_OR_USED_STATUS;
  const hasOtherError =
    (Boolean(accountError) && accountErrorStatus !== INVITE_TOKEN_EXPIRED_OR_USED_STATUS) ||
    (Boolean(directPaymentInfoError) && directPaymentInfoErrorStatus !== INVITE_TOKEN_EXPIRED_OR_USED_STATUS);

  const [postOneTimePayment, { isLoading: isSubmitting }] = usePostOneTimePaymentMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [serverCardNumberError, setServerCardNumberError] = useState<string | null>(null);
  const [serverRoutingNumberError, setServerRoutingNumberError] = useState<string | null>(null);
  const [successDetails, setSuccessDetails] = useState<{ firstName: string; amount: number } | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const recipientApplicantType =
    directPaymentInfo?.applicantType === ApplicantType.secondary ? ApplicantType.secondary : ApplicantType.primary;
  const recipientApplicant = account?.applicants?.find((a) => a.type === recipientApplicantType);
  const address = account?.address;
  const addressLine1 = address?.address1 ?? '';
  const addressLine2 = address ? `${address.city}, ${address.state} ${address.zip}` : '';

  const handlePaymentMethodChange = useCallback((details: { value: string[] }) => {
    const paymentMethodSelected = details.value[0] as PaymentMethod | undefined;
    if (!paymentMethodSelected) return;
    setPaymentMethod(paymentMethodSelected);
    setHasAttemptedSubmit(false);
    setSubmitError(null);
    setServerCardNumberError(null);
    setServerRoutingNumberError(null);
    if (paymentMethodSelected === 'credit') {
      setAccountHolderName('');
      setRoutingNumber('');
      setAccountNumber('');
      setConfirmAccountNumber('');
      setBankAccountType('checking');
    } else {
      setCardNumber('');
      setExpiry('');
      setCvv('');
      setZip('');
      setNameOnCard('');
    }
  }, []);
  const handleCardNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(e.target.value.replace(/\D/g, ''));
    setServerCardNumberError(null);
  }, []);
  const handleExpiryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setExpiry(e.target.value), []);
  const handleCvvChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setCvv(e.target.value.replace(/\D/g, '')),
    [],
  );
  const handleZipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setZip(e.target.value.replace(/\D/g, '')),
    [],
  );
  const handleNameOnCardChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setNameOnCard(e.target.value),
    [],
  );
  const handleRoutingNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRoutingNumber(e.target.value.replace(/\D/g, ''));
    setServerRoutingNumberError(null);
  }, []);
  const handleAccountNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setAccountNumber(e.target.value.replace(/\D/g, '')),
    [],
  );
  const handleConfirmAccountNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setConfirmAccountNumber(e.target.value.replace(/\D/g, '')),
    [],
  );
  const handlePaymentAmountChange = useCallback(
    (details: { value: string[] }) => setPaymentAmountOption(details.value[0] as PaymentAmountOption),
    [],
  );
  const handleCustomAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setCustomAmount(formatCentsInput(e.target.value)),
    [],
  );

  const handlePaymentFrequencyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setPaymentFrequency(e.target.value as 'autopay' | 'oneTime'),
    [],
  );

  const handleBankAccountTypeChange = useCallback((details: { value: string[] }) => {
    if (details.value[0]) setBankAccountType(details.value[0] as 'checking' | 'savings');
  }, []);
  const handleAccountHolderNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setAccountHolderName(e.target.value),
    [],
  );

  const amountPastDue = directPaymentInfo ? parseFloat(directPaymentInfo.amountPastDue) : null;
  const totalAmountDue = directPaymentInfo ? parseFloat(directPaymentInfo.totalDue) : null;

  const paymentAmountItems = useMemo(
    () => [
      {
        label: amountPastDue != null ? `Amount past due: ${formatCurrency(amountPastDue)}` : 'Past due amount',
        value: 'pastDue',
        selected: paymentAmountOption === 'pastDue',
      },
      {
        label: totalAmountDue != null ? `Due today: ${formatCurrency(totalAmountDue)}` : 'Due today',
        value: 'totalDue',
        selected: paymentAmountOption === 'totalDue',
      },
      { label: 'Custom amount', value: 'custom', selected: paymentAmountOption === 'custom' },
    ],
    [amountPastDue, totalAmountDue, paymentAmountOption],
  );

  const paymentAmountValue = useMemo(() => [paymentAmountOption], [paymentAmountOption]);
  const paymentMethodValue = useMemo(() => [paymentMethod], [paymentMethod]);
  const bankAccountTypeValue = useMemo(() => [bankAccountType], [bankAccountType]);
  const bankAccountTypeItems = useMemo(
    () => [
      { label: 'Checking', value: 'checking', selected: bankAccountType === 'checking' },
      { label: 'Savings', value: 'savings', selected: bankAccountType === 'savings' },
    ],
    [bankAccountType],
  );

  const selectedAmountValue =
    paymentAmountOption === 'pastDue'
      ? amountPastDue
      : paymentAmountOption === 'totalDue'
        ? totalAmountDue
        : paymentAmountOption === 'custom' && customAmount
          ? parseFloat(customAmount)
          : null;

  const selectedAmount =
    selectedAmountValue != null && Number.isFinite(selectedAmountValue) ? formatCurrency(selectedAmountValue) : null;

  const amountError = useMemo(() => {
    if (paymentAmountOption !== 'custom') return null;
    if (!customAmount) return null;
    const parsed = parseFloat(customAmount);
    if (!Number.isFinite(parsed)) return 'Enter a valid amount';
    if (parsed < minAllowedPayment) return `Minimum payment is ${formatCurrency(minAllowedPayment)}`;
    return null;
  }, [paymentAmountOption, customAmount, minAllowedPayment]);

  const isAmountValid =
    selectedAmountValue != null && Number.isFinite(selectedAmountValue) && selectedAmountValue >= minAllowedPayment;

  const cardBrand = useMemo(() => detectCardBrand(cardNumber), [cardNumber]);
  const cardLeadingIcon = useMemo(
    () => (cardBrand ? <PalmettoPaymentMethodIcon name={cardBrand} size="lg" /> : undefined),
    [cardBrand],
  );

  const cardNumberError = useMemo(() => {
    if (serverCardNumberError) return serverCardNumberError;
    if (!cardNumber) return hasAttemptedSubmit ? 'Card number is required' : null;
    if (cardNumber.length < 13 || cardNumber.length > 19) return 'Card number must be 13–19 digits';
    return null;
  }, [cardNumber, hasAttemptedSubmit, serverCardNumberError]);

  const nameOnCardError = useMemo(() => {
    if (!nameOnCard) return hasAttemptedSubmit ? 'Name on card is required' : null;
    return null;
  }, [nameOnCard, hasAttemptedSubmit]);

  const cvvError = useMemo(() => {
    if (!cvv) return hasAttemptedSubmit ? 'CVV is required' : null;
    if (cvv.length < 3 || cvv.length > 4) return 'CVV must be 3–4 digits';
    return null;
  }, [cvv, hasAttemptedSubmit]);

  const zipError = useMemo(() => {
    if (!zip) return hasAttemptedSubmit ? 'Zip code is required' : null;
    if (zip.length !== 5) return 'Zip code must be 5 digits';
    return null;
  }, [zip, hasAttemptedSubmit]);

  const accountHolderNameError = useMemo(() => {
    if (!accountHolderName) return hasAttemptedSubmit ? 'Account holder name is required' : null;
    return null;
  }, [accountHolderName, hasAttemptedSubmit]);

  const routingError = useMemo(() => {
    if (serverRoutingNumberError) return serverRoutingNumberError;
    if (!routingNumber) return hasAttemptedSubmit ? 'Routing number is required' : null;
    if (routingNumber.length !== 9) return 'Routing number must be 9 digits';
    if (!/^[0123]/.test(routingNumber)) return 'Routing number must start with 0, 1, 2, or 3';
    return null;
  }, [routingNumber, hasAttemptedSubmit, serverRoutingNumberError]);

  const accountNumberError = useMemo(() => {
    if (!accountNumber) return hasAttemptedSubmit ? 'Account number is required' : null;
    if (accountNumber.length < 4 || accountNumber.length > 17) return 'Account number must be 4–17 digits';
    return null;
  }, [accountNumber, hasAttemptedSubmit]);

  const confirmAccountNumberError = useMemo(() => {
    if (!confirmAccountNumber) return hasAttemptedSubmit ? 'Please confirm the account number' : null;
    if (confirmAccountNumber !== accountNumber) return 'Account numbers do not match';
    return null;
  }, [confirmAccountNumber, accountNumber, hasAttemptedSubmit]);

  const expiryError = useMemo(() => {
    if (!expiry) return hasAttemptedSubmit ? 'Expiration is required' : null;
    const match = expiry.match(/^(\d{2})\/(\d{4})$/);
    if (!match) return 'Enter as MM/YYYY';
    const month = parseInt(match[1], 10);
    const year = parseInt(match[2], 10);
    if (month < 1 || month > 12) return 'Invalid month';
    const now = DateTime.utc();
    if (year < now.year || (year === now.year && month < now.month)) return 'Card is expired';
    return null;
  }, [expiry, hasAttemptedSubmit]);

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true);
    setSubmitError(null);
    setServerCardNumberError(null);
    setServerRoutingNumberError(null);

    if (paymentFrequency === null) {
      // validationMessage on the option group surfaces the requirement
      return;
    }

    if (
      selectedAmountValue == null ||
      !Number.isFinite(selectedAmountValue) ||
      selectedAmountValue < minAllowedPayment
    ) {
      setSubmitError(`Minimum payment is ${formatCurrency(minAllowedPayment)}`);
      return;
    }

    const paymentDate = getConcordPaymentDate();

    const base = {
      accountId: id,
      amount: selectedAmountValue,
      paymentDate,
      inviteToken,
      enrollInAutoPay: paymentFrequency === 'autopay',
    };

    let payload: Parameters<typeof postOneTimePayment>[0];
    if (paymentMethod === 'credit') {
      if (!nameOnCard || !cardNumber || !expiry || !cvv || !zip) {
        setSubmitError('Please fill in all card details');
        return;
      }
      const cardFieldError = cardNumberError ?? expiryError ?? cvvError ?? zipError;
      if (cardFieldError) {
        setSubmitError(cardFieldError);
        return;
      }
      const expiryMatch = expiry.match(/^(\d{2})\/(\d{4})$/);
      if (!expiryMatch) {
        setSubmitError('Enter a valid expiration (MM/YYYY)');
        return;
      }
      const [, expirationMonth, expirationYear] = expiryMatch;
      payload = {
        ...base,
        card: {
          number: cardNumber,
          expirationMonth,
          expirationYear,
          cvc: cvv,
          cardHolderName: nameOnCard,
          zipCode: zip,
        },
      };
    } else {
      if (!accountHolderName || !routingNumber || !accountNumber || !confirmAccountNumber) {
        setSubmitError('Please fill in all bank account details');
        return;
      }
      const achFieldError = routingError ?? accountNumberError ?? confirmAccountNumberError;
      if (achFieldError) {
        setSubmitError(achFieldError);
        return;
      }
      payload = {
        ...base,
        bankAccount: {
          accountNumber,
          routingNumber,
          bankAccountType,
          accountHolderName,
        },
      };
    }

    try {
      await postOneTimePayment(payload).unwrap();
      setSuccessDetails({ firstName: recipientApplicant?.firstName ?? '', amount: selectedAmountValue });
    } catch (err: unknown) {
      const message = (err as { data?: { message?: string } })?.data?.message ?? 'Payment failed. Please try again.';
      setSubmitError(message);
      const lower = message.toLowerCase();
      if (lower.includes('credit card number')) {
        setServerCardNumberError(message);
      }
      if (lower.includes('routing number')) {
        setServerRoutingNumberError(message);
      }
    }
  }, [
    selectedAmountValue,
    id,
    inviteToken,
    paymentMethod,
    expiry,
    expiryError,
    cardNumber,
    cardNumberError,
    cvv,
    cvvError,
    zip,
    zipError,
    nameOnCard,
    routingNumber,
    routingError,
    accountNumber,
    accountNumberError,
    confirmAccountNumber,
    confirmAccountNumberError,
    bankAccountType,
    accountHolderName,
    recipientApplicant?.firstName,
    postOneTimePayment,
    minAllowedPayment,
    paymentFrequency,
  ]);

  if (isLoading || isDirectPaymentInfoLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-default">
        <div className="body-200 text-text-secondary">Loading...</div>
      </div>
    );
  }

  if (successDetails) {
    return <CustomerDirectPaymentSuccess firstName={successDetails.firstName} amount={successDetails.amount} />;
  }

  if (hasOtherError) {
    return <CustomerDirectPaymentError />;
  }

  if (isLinkInvalid) {
    return <CustomerDirectPaymentExpiredLink />;
  }

  return (
    <div data-mode="light">
      <div data-theme="core" className="flex min-h-screen flex-col bg-canvas-default px-24 py-40">
        {/* Hide DSM-rendered label text above each input — placeholder text handles UX */}
        <style>{`.dp-inputs label { display: none !important; }`}</style>

        {/* Logo — pinned to top */}
        <div className="flex justify-center">
          <img src="/palmetto-brand-plain-logo.svg" alt="Palmetto" style={LOGO_STYLE} />
        </div>

        {/* Main content — centered vertically in remaining space */}
        <div className="flex flex-1 items-center justify-center py-40">
          <div className="flex w-full max-w-[760px] flex-col">
            {/* Top two-column section: info card + payment form */}
            <div className="mb-24 flex flex-col gap-16 lg:flex-row lg:items-stretch lg:gap-24">
              {/* Customer info card */}
              <div className="dp-card flex w-full flex-col gap-16 rounded-32 bg-canvas-bold p-28 lg:w-[340px] lg:shrink-0">
                <h2 className="heading-400 text-text-primary">
                  Make a <span className="whitespace-nowrap">one-time</span> payment
                </h2>

                {/* Customer info — bottom-anchored so the card splits title (top) / info (bottom) */}
                <div className="flex flex-col gap-16 lg:mt-auto">
                  {addressLine1 && (
                    <div>
                      <p className="body-bold-400 text-text-secondary">Property Address</p>
                      <p className="body-400 text-text-secondary">{addressLine1}</p>
                      {addressLine2 && <p className="body-400 text-text-secondary">{addressLine2}</p>}
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between py-4">
                      <span className="body-bold-400 text-text-secondary">Amount past due</span>
                      <span className="body-bold-400 text-text-secondary">
                        {amountPastDue != null ? formatCurrency(amountPastDue) : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-4">
                      <span className="body-bold-400 text-text-secondary">Due today</span>
                      <span className="body-bold-400 text-text-secondary">
                        {totalAmountDue != null ? formatCurrency(totalAmountDue) : '—'}
                      </span>
                    </div>
                  </div>

                  <PalmettoSelect
                    label="Payment amount"
                    value={paymentAmountValue}
                    onValueChange={handlePaymentAmountChange}
                    items={paymentAmountItems}
                  />

                  {paymentAmountOption === 'custom' && (
                    <div className="dp-inputs mt-10">
                      <PalmettoTextInput
                        label="Amount to pay today"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        onBeforeInput={digitsOnlyBeforeInput}
                        placeholder="Amount to pay today"
                        inputMode="numeric"
                        prefix="$"
                        validationMessage={amountError ? { type: 'error', text: amountError } : undefined}
                      />
                      {!amountError && (
                        <p className="mt-6 body-200 text-input-content-secondary">
                          Minimum payment is {formatCurrency(minAllowedPayment)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Payment form */}
              <div className="flex-1">
                {/* Payment method tabs */}
                <div className="mb-20">
                  <PalmettoToggleGroup
                    value={paymentMethodValue}
                    onValueChange={handlePaymentMethodChange}
                    items={PAYMENT_METHOD_ITEMS}
                  />
                </div>

                {paymentMethod === 'credit' ? (
                  <CustomerDirectPaymentCardForm
                    cardNumber={cardNumber}
                    onCardNumberChange={handleCardNumberChange}
                    cardLeadingIcon={cardLeadingIcon}
                    cardNumberError={cardNumberError}
                    expiry={expiry}
                    onExpiryChange={handleExpiryChange}
                    expiryError={expiryError}
                    cvv={cvv}
                    onCvvChange={handleCvvChange}
                    cvvError={cvvError}
                    zip={zip}
                    onZipChange={handleZipChange}
                    zipError={zipError}
                    nameOnCard={nameOnCard}
                    onNameOnCardChange={handleNameOnCardChange}
                    nameOnCardError={nameOnCardError}
                  />
                ) : (
                  <CustomerDirectPaymentAchForm
                    accountHolderName={accountHolderName}
                    onAccountHolderNameChange={handleAccountHolderNameChange}
                    accountHolderNameError={accountHolderNameError}
                    routingNumber={routingNumber}
                    onRoutingNumberChange={handleRoutingNumberChange}
                    routingError={routingError}
                    accountNumber={accountNumber}
                    onAccountNumberChange={handleAccountNumberChange}
                    accountNumberError={accountNumberError}
                    confirmAccountNumber={confirmAccountNumber}
                    onConfirmAccountNumberChange={handleConfirmAccountNumberChange}
                    confirmAccountNumberError={confirmAccountNumberError}
                    bankAccountTypeValue={bankAccountTypeValue}
                    onBankAccountTypeChange={handleBankAccountTypeChange}
                    bankAccountTypeItems={bankAccountTypeItems}
                  />
                )}

                {/* Payment frequency — below the card/ACH fields, matching column width */}
                <div className="mt-20">
                  <PalmettoOptionGroup
                    type="radio"
                    label="Payment frequency"
                    name="paymentFrequency"
                    value={paymentFrequency ?? ''}
                    onChange={handlePaymentFrequencyChange}
                    options={PAYMENT_FREQUENCY_OPTIONS}
                    validationMessage={
                      hasAttemptedSubmit && paymentFrequency === null
                        ? { type: 'error', text: 'Please select a payment frequency' }
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>

            {/* Full-width CTA button */}
            <PalmettoButton
              type="button"
              emphasis="primary"
              size="medium"
              layout="fill"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting || !isAmountValid}
            >
              {selectedAmount ? `Make a ${selectedAmount} payment` : 'Make a payment'}
            </PalmettoButton>

            {submitError && <p className="mt-8 text-center body-400 text-text-error">{submitError}</p>}

            {/* Security notice */}
            <div className="mt-16 flex items-center justify-center gap-8">
              <PalmettoIcon name="lockCheck" size="sm" className="text-text-secondary" />
              <span className="body-200 text-text-secondary">
                Your payment information stays encrypted and secure
              </span>
            </div>

            {/* Processing fee disclaimer — credit only */}
            {paymentMethod === 'credit' && (
              <p className="mt-8 text-center body-200 text-text-info">
                A processing fee for credit cards may be added to your payment. No fees for debit cards.
              </p>
            )}
          </div>
        </div>

        {/* Expiry — pinned to bottom */}
        <div className="flex justify-center">
          <ExpiryNotice expiresAt={directPaymentInfo?.expiresAt ?? null} />
        </div>
      </div>
    </div>
  );
}

function ExpiryNotice({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return null;

  const expiresDisplay = DateTime.fromISO(expiresAt).toLocal().toFormat("MMMM d, yyyy 'at' h:mma");

  return (
    <div className="flex items-center justify-center gap-8">
      <PalmettoIcon name="clockSquare" size="xs" className="text-text-tertiary" />
      <span className="body-200 text-text-tertiary">This payment link expires {expiresDisplay}</span>
    </div>
  );
}
